<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import DashboardFilterTree from '@/modules/dashboard/components/DashboardFilterTree.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import { useGraphStore } from '@/modules/dashboard/stores/graphStore'
import chevron_left from '@images/icons/chevron_left.svg?raw'
import { viconSVGHelper } from '@/utils/ViconSVGHelper'
import type { IFilterMeta } from '@/modules/dashboard/types/graph/IGraphResponse'
import type { IGraphTarget } from '@/modules/dashboard/types/graph/IGraphTarget'
import { useFilterPresets } from '@/modules/dashboard/composables/useFilterPresets'
import type { IFilterPreset } from '@/modules/dashboard/types/IFilterPreset'

interface Props {
  target: IGraphTarget
  filters: IFilterMeta[]
  loading?: boolean
  position?: 'left' | 'right'
  isDrawerOpen?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  isDrawerOpen: false,
})

const emit = defineEmits<{
  (e: 'update:position', value: 'left' | 'right'): void
  (e: 'update:isDrawerOpen', value: boolean): void
}>()

const graphStore = useGraphStore()
const display = useDisplay()

const localIsDrawerOpen = computed({
  get: () => props.isDrawerOpen,
  set: (val: boolean) => emit('update:isDrawerOpen', val),
})

// Базовые SVG иконки
const collapseIcon = viconSVGHelper(chevron_left) as any
const expandIcon = viconSVGHelper(chevron_left, 180) as any

// Константы для localStorage
const STORAGE_WIDTH_KEY = 'materio.pane-width.dashboard-filters'
const STORAGE_COLLAPSED_KEY = 'materio.pane-collapsed.dashboard-filters'
const STORAGE_POSITION_KEY = 'materio.pane-position.dashboard-filters'
const STORAGE_DEFAULT_PANELS_EXPANDED_KEY = 'materio.pane-panels-expanded.dashboard-filters'
const MIN_WIDTH = 260
const MAX_WIDTH = 600
const DEFAULT_WIDTH = 320
const COLLAPSED_WIDTH = 52

// Чтение сохраненной ширины из localStorage
const readStoredWidth = (): number => {
  try {
    const raw = localStorage.getItem(STORAGE_WIDTH_KEY)
    if (raw) {
      const n = Number(raw)
      if (Number.isFinite(n) && n >= MIN_WIDTH && n <= MAX_WIDTH) return n
    }
  } catch {}
  return DEFAULT_WIDTH
}

// Чтение сохраненного состояния сворачивания
const readStoredCollapsed = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_COLLAPSED_KEY) === 'true'
  } catch {}
  return false
}

// Чтение сохраненного положения сайдбара (left / right)
const readStoredPosition = (): 'left' | 'right' => {
  try {
    const raw = localStorage.getItem(STORAGE_POSITION_KEY)
    if (raw === 'left' || raw === 'right') return raw
  } catch {}
  return 'right'
}

// Чтение сохраненного дефолтного состояния раскрытия панелей аккордеона
const readStoredDefaultPanelsExpanded = (): boolean => {
  try {
    const raw = localStorage.getItem(STORAGE_DEFAULT_PANELS_EXPANDED_KEY)
    if (raw !== null) return raw === 'true'
  } catch {}
  return false
}

const sidebarWidth = ref(readStoredWidth())
const isCollapsed = ref(readStoredCollapsed())
const sidebarPosition = ref<'left' | 'right'>(props.position || readStoredPosition())
const isDefaultPanelsExpanded = ref(readStoredDefaultPanelsExpanded())
const isResizing = ref(false)

const { getPresetsForTarget, renamePreset, deletePreset, isPresetActive } = useFilterPresets()
const targetPresets = getPresetsForTarget(props.target)
const presetSearch = ref('')

const filteredPresets = computed(() => {
  const term = presetSearch.value.trim().toLowerCase()
  if (!term) return targetPresets.value
  return targetPresets.value.filter(p => p.label.toLowerCase().includes(term))
})

const isEditPresetDialogOpen = ref(false)
const selectedPresetForEdit = ref<IFilterPreset | null>(null)
const editingPresetLabel = ref('')
const editPresetError = ref('')

const openEditPresetDialog = (preset: IFilterPreset) => {
  selectedPresetForEdit.value = preset
  editingPresetLabel.value = preset.label
  editPresetError.value = ''
  isEditPresetDialogOpen.value = true
}

const handleUpdatePreset = () => {
  if (!selectedPresetForEdit.value) return
  const trimmed = editingPresetLabel.value.trim()
  if (!trimmed) {
    editPresetError.value = 'Введите название'
    return
  }
  renamePreset(selectedPresetForEdit.value.id, trimmed)
  isEditPresetDialogOpen.value = false
}

const handleDeletePreset = () => {
  if (!selectedPresetForEdit.value) return
  deletePreset(selectedPresetForEdit.value.id)
  isEditPresetDialogOpen.value = false
}

const hasActivePreset = computed(() => {
  return targetPresets.value.some(p =>
    isPresetActive(p, graphStore.activeFilters, graphStore.groupBy, graphStore.dateTrunc),
  )
})

const activePresetLabels = computed(() => {
  const active = targetPresets.value.filter(p =>
    isPresetActive(p, graphStore.activeFilters, graphStore.groupBy, graphStore.dateTrunc),
  )
  return active.map(p => p.label).join(', ')
})

const togglePreset = async (preset: IFilterPreset) => {
  const active = isPresetActive(preset, graphStore.activeFilters, graphStore.groupBy, graphStore.dateTrunc)
  if (active) {
    await graphStore.resetGraphFilters(props.target)
  } else {
    graphStore.activeFilters = JSON.parse(JSON.stringify(preset.filters))
    graphStore.groupBy = preset.groupBy || null
    graphStore.dateTrunc = preset.dateTrunc || null

    await Promise.all([
      graphStore.fetchGraph(props.target),
      graphStore.refetchAllFilterChoices(props.target),
    ])
  }
}

const getAllPanelKeys = (filters = props.filters) => {
  const keys = filters.map(f => f.key)
  if (targetPresets.value.length > 0) {
    keys.unshift('__saved_presets__')
  }
  return keys
}

// Состояние открытых секций аккордеона
const openedPanels = ref<string[]>(isDefaultPanelsExpanded.value ? getAllPanelKeys() : (targetPresets.value.length > 0 ? ['__saved_presets__'] : []))

watch(
  () => targetPresets.value.length,
  (newLen, oldLen) => {
    if (newLen > 0 && (!oldLen || oldLen === 0)) {
      if (!openedPanels.value.includes('__saved_presets__')) {
        openedPanels.value = ['__saved_presets__', ...openedPanels.value]
      }
    }
  },
)

const toggleExpandAllPanels = () => {
  const allKeys = getAllPanelKeys()
  if (openedPanels.value.length > 0) {
    openedPanels.value = []
    isDefaultPanelsExpanded.value = false
  } else {
    openedPanels.value = allKeys
    isDefaultPanelsExpanded.value = true
  }
  try {
    localStorage.setItem(STORAGE_DEFAULT_PANELS_EXPANDED_KEY, String(isDefaultPanelsExpanded.value))
  } catch {}
}

const expandAllPanelsIcon = computed(() => {
  return openedPanels.value.length > 0 ? 'ri-collapse-vertical-line' : 'ri-expand-vertical-line'
})

const expandAllPanelsTooltip = computed(() => {
  return openedPanels.value.length > 0
    ? 'Свернуть все панели'
    : 'Развернуть все панели'
})

watch(
  () => props.position,
  val => {
    if (val && (val === 'left' || val === 'right') && val !== sidebarPosition.value) {
      sidebarPosition.value = val
    }
  },
)

const togglePosition = () => {
  const nextPos = sidebarPosition.value === 'left' ? 'right' : 'left'
  sidebarPosition.value = nextPos
  try {
    localStorage.setItem(STORAGE_POSITION_KEY, nextPos)
  } catch {}
  emit('update:position', nextPos)
}

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value
  try {
    localStorage.setItem(STORAGE_COLLAPSED_KEY, String(isCollapsed.value))
  } catch {}
}

// Иконки сворачивания/разворачивания зависят от положения сайдбара
const currentCollapseIcon = computed(() => {
  return sidebarPosition.value === 'left' ? collapseIcon : expandIcon
})

const currentExpandIcon = computed(() => {
  return sidebarPosition.value === 'left' ? expandIcon : collapseIcon
})

const positionToggleIcon = computed(() => {
  return sidebarPosition.value === 'left' ? 'ri-layout-right-2-line' : 'ri-layout-left-2-line'
})

const positionToggleTitle = computed(() => {
  return sidebarPosition.value === 'left' ? 'Перенести панель вправо' : 'Перенести панель влево'
})

// Логика перетаскивания (Resizing)
let startX = 0
let startW = 0

const onMouseMove = (e: MouseEvent) => {
  const delta = sidebarPosition.value === 'right' ? (startX - e.clientX) : (e.clientX - startX)
  const newW = Math.min(Math.max(startW + delta, MIN_WIDTH), MAX_WIDTH)
  sidebarWidth.value = newW
  try {
    localStorage.setItem(STORAGE_WIDTH_KEY, String(Math.round(newW)))
  } catch {}
}

const onMouseUp = () => {
  isResizing.value = false
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
}

const startResize = (e: MouseEvent) => {
  if (isCollapsed.value) return
  e.preventDefault()
  startX = e.clientX
  startW = sidebarWidth.value
  isResizing.value = true
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
})

// Состояние выбранных элементов для каждого фильтра (синхронизируется со стором)
const selectedFilterIds = computed<Record<string, string[]>>(() => {
  const result: Record<string, string[]> = {}
  for (const filter of props.filters) {
    const active = graphStore.activeFilters[filter.key]
    if (Array.isArray(active)) {
      result[filter.key] = active.map(item => String(item.id))
    } else if (active && typeof active === 'object' && 'id' in active) {
      result[filter.key] = [String((active as any).id)]
    } else {
      result[filter.key] = []
    }
  }
  return result
})

// При первом получении или обновлении фильтров раскрываем панели и гарантированно грузим данные
watch(
  [() => props.filters, () => props.target],
  ([filters], oldVals) => {
    const oldTarget = oldVals ? oldVals[1] : undefined
    if (filters && filters.length) {
      if (oldTarget && props.target !== oldTarget) {
        openedPanels.value = isDefaultPanelsExpanded.value ? filters.map(f => f.key) : []
      } else if (openedPanels.value.length === 0 && isDefaultPanelsExpanded.value) {
        openedPanels.value = filters.map(f => f.key)
      }
      filters.forEach(f => {
        if (!graphStore.filterChoices[f.key]?.length && !graphStore.filterLoading[f.key]) {
          graphStore.fetchFilterChoices(f.key, '', undefined, undefined, props.target)
        }
      })
    }
  },
  { immediate: true, deep: true },
)

// При изменении любого активного фильтра перезапрашиваем choices для всех панелей
watch(
  () => graphStore.activeFilters,
  () => {
    graphStore.refetchAllFilterChoices(props.target)
  },
  { deep: true },
)

const onPanelChange = (newPanels: any) => {
  const keys = Array.isArray(newPanels) ? newPanels : [newPanels]
  keys.forEach(key => {
    if (key && key !== '__saved_presets__' && !graphStore.filterChoices[key]?.length && !graphStore.filterLoading[key]) {
      graphStore.fetchFilterChoices(key, '', undefined, undefined, props.target)
    }
  })
}

const getSelectedCount = (key: string): number => {
  const active = graphStore.activeFilters[key]
  if (Array.isArray(active)) return active.length
  return active ? 1 : 0
}

const getFilterSelectedLabels = (filterKey: string): string => {
  const active = graphStore.activeFilters[filterKey]
  if (!active) return ''
  if (Array.isArray(active)) {
    return active.map(item => item.label || String(item.id)).join(', ')
  }
  if (typeof active === 'object' && 'label' in active) {
    return (active as any).label || String((active as any).id)
  }
  return String(active)
}

const openFilterFromRail = (filterKey: string) => {
  isCollapsed.value = false
  try {
    localStorage.setItem(STORAGE_COLLAPSED_KEY, 'false')
  } catch {}
  if (!openedPanels.value.includes(filterKey)) {
    openedPanels.value = [...openedPanels.value, filterKey]
  }
}

const toggleGroupBy = async (key: string) => {
  if (graphStore.groupBy === key) {
    await graphStore.setGroupBy(props.target, null)
  } else {
    await graphStore.setGroupBy(props.target, key)
  }
}

const onFilterChange = async (fieldKey: string, newSelectedIds: string[]) => {
  if (newSelectedIds.length >= 2 && !graphStore.groupBy) {
    graphStore.groupBy = fieldKey
  } else if (newSelectedIds.length < 2 && graphStore.groupBy === fieldKey) {
    graphStore.groupBy = null
  }
  await graphStore.updateFilterTreeSelection(props.target, fieldKey, newSelectedIds)
}

const onResetAll = async () => {
  await graphStore.resetGraphFilters(props.target)
}

const onResetFilter = async (key: string) => {
  await graphStore.removeGraphFilter(props.target, key)
}

const activeFiltersCount = computed(() => graphStore.activeFilterChips.length)
const activeFiltersList = computed(() => props.filters.filter(f => getSelectedCount(f.key) > 0))
</script>

<template>
  <!-- ДЕСКТОПНАЯ ВЕРСИЯ (ПК): 100% БЕЗ ИЗМЕНЕНИЙ -->
  <div
    v-if="!display.smAndDown.value"
    class="dashboard-filter-sidebar-wrapper d-flex align-stretch"
    :class="{
      'is-collapsed': isCollapsed,
      'is-resizing': isResizing,
      'position-left': sidebarPosition === 'left',
      'position-right': sidebarPosition === 'right',
    }"
    :style="{
      width: isCollapsed ? `${COLLAPSED_WIDTH}px` : `${sidebarWidth}px`,
      flexBasis: isCollapsed ? `${COLLAPSED_WIDTH}px` : `${sidebarWidth}px`,
    }"
  >
    <!-- Ресайзер слева при расположении сайдбара справа -->
    <div
      v-if="!isCollapsed && !display.smAndDown.value && sidebarPosition === 'right'"
      class="filter-sidebar-resizer resizer-left"
      :class="{ 'is-resizing': isResizing }"
      @mousedown="startResize"
    />

    <!-- Карточка сайдбара -->
    <VCard
      class="sidebar-card flex-grow-1 d-flex flex-column"
      elevation="0"
    >
      <!-- Единая шапка сайдбара с динамической иконкой сворачивания/разворачивания -->
      <div
        class="sidebar-header d-flex align-center"
        :class="isCollapsed ? 'justify-center cursor-pointer' : 'justify-space-between'"
        @click="isCollapsed ? toggleCollapse() : undefined"
      >
        <!-- Развернутое состояние шапки -->
        <template v-if="!isCollapsed">
          <!-- Когда сайдбар СЛЕВА -->
          <template v-if="sidebarPosition === 'left'">
            <div class="d-flex align-center gap-2 text-truncate">
              <VIcon icon="ri-filter-3-line" size="20" color="primary" />
              <span class="text-subtitle-1 font-weight-bold text-truncate">Фильтры</span>
              <VChip
                v-if="activeFiltersCount > 0"
                size="x-small"
                color="primary"
                variant="flat"
              >
                {{ activeFiltersCount }}
              </VChip>
            </div>

            <div class="d-flex align-center gap-1">
              <!-- Кнопка развернуть / свернуть все панели -->
              <IconBtn
                size="small"
                class="sidebar-expand-all-toggle"
                @click.stop="toggleExpandAllPanels"
              >
                <VIcon :icon="expandAllPanelsIcon" size="20" />
                <VTooltip activator="parent" location="bottom">
                  {{ expandAllPanelsTooltip }}
                </VTooltip>
              </IconBtn>

              <!-- Кнопка смены расположения сайдбара (лево / право) -->
              <IconBtn
                size="small"
                class="sidebar-pos-toggle"
                @click.stop="togglePosition"
              >
                <VIcon :icon="positionToggleIcon" size="20" />
                <VTooltip activator="parent" location="bottom">
                  {{ positionToggleTitle }}
                </VTooltip>
              </IconBtn>

              <!-- Иконка сворачивания сайдбара -->
              <div
                class="sidebar-nav-toggle nav-pin cursor-pointer d-flex align-center justify-center"
                @click.stop="toggleCollapse"
              >
                <VIcon :icon="currentCollapseIcon" size="24" />
                <VTooltip activator="parent" location="bottom">
                  Свернуть фильтры
                </VTooltip>
              </div>
            </div>
          </template>

          <!-- Когда сайдбар СПРАВА -->
          <template v-else>
            <div class="d-flex align-center gap-1">
              <!-- Иконка сворачивания сайдбара -->
              <div
                class="sidebar-nav-toggle nav-pin cursor-pointer d-flex align-center justify-center"
                @click.stop="toggleCollapse"
              >
                <VIcon :icon="currentCollapseIcon" size="24" />
                <VTooltip activator="parent" location="bottom">
                  Свернуть фильтры
                </VTooltip>
              </div>

              <!-- Кнопка смены расположения сайдбара (лево / право) -->
              <IconBtn
                size="small"
                class="sidebar-pos-toggle"
                @click.stop="togglePosition"
              >
                <VIcon :icon="positionToggleIcon" size="20" />
                <VTooltip activator="parent" location="bottom">
                  {{ positionToggleTitle }}
                </VTooltip>
              </IconBtn>

              <!-- Кнопка развернуть / свернуть все панели -->
              <IconBtn
                size="small"
                class="sidebar-expand-all-toggle"
                @click.stop="toggleExpandAllPanels"
              >
                <VIcon :icon="expandAllPanelsIcon" size="20" />
                <VTooltip activator="parent" location="bottom">
                  {{ expandAllPanelsTooltip }}
                </VTooltip>
              </IconBtn>
            </div>

            <div class="d-flex align-center gap-2 text-truncate">
              <VIcon icon="ri-filter-3-line" size="20" color="primary" />
              <span class="text-subtitle-1 font-weight-bold text-truncate">Фильтры</span>
              <VChip
                v-if="activeFiltersCount > 0"
                size="x-small"
                color="primary"
                variant="flat"
              >
                {{ activeFiltersCount }}
              </VChip>
            </div>
          </template>
        </template>

        <!-- Свернутое состояние шапки: стрелка разворачивания -->
        <template v-else>
          <div
            class="sidebar-nav-toggle nav-unpin cursor-pointer d-flex align-center justify-center"
            @click.stop="toggleCollapse"
          >
            <VIcon :icon="currentExpandIcon" size="24" />
            <VTooltip activator="parent" :location="sidebarPosition === 'left' ? 'end' : 'start'">
              Развернуть фильтры
            </VTooltip>
          </div>
        </template>
      </div>

      <VDivider />

      <!-- Свернутое тело рейла: только выбранные фильтры + кнопка сброса -->
      <div
        v-if="isCollapsed"
        class="collapsed-rail d-flex flex-column align-center py-3 gap-2 flex-grow-1"
      >
        <!-- Иконка сохраненных фильтров в рельсе -->
        <template v-if="targetPresets.length > 0">
          <div
            class="rail-filter-item d-flex justify-center align-center cursor-pointer"
            :class="{ 'has-active': hasActivePreset }"
            @click.stop="openFilterFromRail('__saved_presets__')"
          >
            <VBadge
              :content="targetPresets.length"
              :color="hasActivePreset ? 'primary' : 'secondary'"
              offset-x="-2"
              offset-y="-2"
            >
              <VIcon
                icon="ri-save-line"
                size="20"
                :color="hasActivePreset ? 'primary' : undefined"
              />
            </VBadge>
            <VTooltip
              activator="parent"
              :location="sidebarPosition === 'left' ? 'end' : 'start'"
            >
              <div class="d-flex flex-column gap-1 py-1" style="max-width: 260px; line-height: 1.35;">
                <span class="font-weight-bold">Сохраненные фильтры</span>
                <span class="font-weight-medium">
                  {{ activePresetLabels || `Всего сохранено: ${targetPresets.length}` }}
                </span>
              </div>
            </VTooltip>
          </div>
          <VDivider class="my-1 w-75" />
        </template>

        <!-- Если есть выбранные фильтры: выводим иконки с бейджами и тултипами -->
        <template v-if="activeFiltersList.length > 0">
          <template v-for="filter in activeFiltersList" :key="filter.key">
            <div
              class="rail-filter-item has-active d-flex justify-center align-center cursor-pointer"
              @click.stop="openFilterFromRail(filter.key)"
            >
              <VBadge
                :content="getSelectedCount(filter.key)"
                color="primary"
                offset-x="-2"
                offset-y="-2"
              >
                <VIcon
                  :icon="filter.icon || 'ri-filter-3-line'"
                  size="20"
                  color="primary"
                />
              </VBadge>
              <VTooltip
                activator="parent"
                :location="sidebarPosition === 'left' ? 'end' : 'start'"
              >
                <div class="d-flex flex-column gap-1 py-1" style="max-width: 260px; line-height: 1.35;">
                  <span class="font-weight-bold">{{ filter.label }}</span>
                  <span class="font-weight-medium">
                    {{ getFilterSelectedLabels(filter.key) }}
                  </span>
                </div>
              </VTooltip>
            </div>
          </template>

          <VDivider class="my-1 w-75" />

          <!-- Кнопка сброса всех фильтров -->
          <div
            class="rail-filter-item rail-reset-item d-flex justify-center align-center cursor-pointer"
            @click.stop="onResetAll"
          >
            <VIcon
              icon="ri-delete-bin-line"
              size="20"
              color="error"
            />
            <VTooltip
              activator="parent"
              :location="sidebarPosition === 'left' ? 'end' : 'start'"
            >
              Сбросить фильтры
            </VTooltip>
          </div>
        </template>

        <!-- Если ничего не выбрано: стандартная иконка фильтра -->
        <template v-else>
          <div
            class="rail-filter-item d-flex justify-center align-center cursor-pointer"
            @click.stop="toggleCollapse"
          >
            <VIcon
              icon="ri-filter-3-line"
              size="20"
              class="rail-inactive-icon"
            />
            <VTooltip
              activator="parent"
              :location="sidebarPosition === 'left' ? 'end' : 'start'"
            >
              Открыть фильтры
            </VTooltip>
          </div>
        </template>
      </div>

      <!-- Развернутый контент сайдбара: единый список фильтров со сквозной прокруткой -->
      <div v-show="!isCollapsed" class="sidebar-content flex-grow-1">
        <!-- Скелетоны при начальной загрузке -->
        <div v-if="loading && (!filters || !filters.length)" class="pa-4 d-flex flex-column gap-3">
          <VSkeletonLoader v-for="i in 3" :key="i" type="heading" class="rounded" />
        </div>

        <!-- Сообщение об отсутствии фильтров -->
        <div
          v-else-if="!filters || !filters.length"
          class="pa-4 text-caption text-disabled text-center"
        >
          Нет доступных фильтров
        </div>

        <!-- Аккордеон фильтров -->
        <VExpansionPanels
          v-else
          v-model="openedPanels"
          multiple
          variant="accordion"
          class="filter-expansion-panels"
          @update:model-value="onPanelChange"
        >
          <!-- Аккордеон: Сохраненные фильтры (если есть сохраненные пресеты) -->
          <VExpansionPanel
            v-if="targetPresets.length > 0"
            value="__saved_presets__"
            class="filter-panel"
          >
            <VExpansionPanelTitle class="py-2 px-4">
              <div class="d-flex align-center justify-space-between w-100 me-2 min-w-0">
                <div class="d-flex align-center min-w-0 flex-grow-1 me-1">
                  <VIcon
                    icon="ri-save-line"
                    size="18"
                    class="me-2 text-medium-emphasis flex-shrink-0"
                  />
                  <span class="filter-panel-title">Сохраненные фильтры</span>
                </div>
                <div class="d-flex align-center gap-1 flex-shrink-0">
                  <VChip
                    size="x-small"
                    color="primary"
                    variant="tonal"
                    class="font-weight-medium"
                  >
                    {{ targetPresets.length }}
                  </VChip>
                  <IconBtn
                    v-if="hasActivePreset || activeFiltersCount > 0"
                    size="x-small"
                    color="secondary"
                    variant="text"
                    @click.stop="onResetAll"
                  >
                    <VIcon icon="ri-delete-bin-line" size="16" />
                    <VTooltip activator="parent" location="bottom">
                      Сбросить фильтр
                    </VTooltip>
                  </IconBtn>
                </div>
              </div>
            </VExpansionPanelTitle>

            <VExpansionPanelText class="pa-0">
              <!-- Поле поиска для сохраненных фильтров -->
              <div class="filter-search-wrapper px-3 pt-2 pb-1">
                <VTextField
                  v-model="presetSearch"
                  placeholder="Поиск (сохраненные фильтры)..."
                  variant="outlined"
                  density="compact"
                  :append-inner-icon="presetSearch ? undefined : 'ri-search-line'"
                  hide-details
                  clearable
                  class="search-field mb-2"
                  @click:clear="presetSearch = ''"
                />
              </div>

              <div class="v-list custom-treeview px-2 pb-1">
                <div
                  v-for="preset in filteredPresets"
                  :key="preset.id"
                  class="v-list-item filter-tree-node d-flex align-center py-1 px-1 rounded cursor-pointer"
                  :class="{
                    'v-list-item--active': isPresetActive(preset, graphStore.activeFilters, graphStore.groupBy, graphStore.dateTrunc),
                    'tree-item--selected': isPresetActive(preset, graphStore.activeFilters, graphStore.groupBy, graphStore.dateTrunc),
                    'tree-item--leaf': true,
                  }"
                  style="padding-inline-start: 6px !important;"
                  @click.stop="togglePreset(preset)"
                >
                  <!-- Фиксированный спейсер листа для выравнивания с чекбоксами дерева -->
                  <div class="v-list-item-action v-list-item-action--start me-1 d-flex align-center justify-center flex-shrink-0">
                    <div class="node-leaf-spacer" />
                  </div>

                  <!-- Чекбокс выбора -->
                  <div class="node-checkbox-wrapper me-2" @click.stop="togglePreset(preset)">
                    <VCheckboxBtn
                      :model-value="isPresetActive(preset, graphStore.activeFilters, graphStore.groupBy, graphStore.dateTrunc)"
                      color="primary"
                      density="compact"
                      class="node-checkbox"
                    />
                  </div>

                  <!-- Название пресета -->
                  <div class="v-list-item__content flex-grow-1 min-width-0">
                    <div class="v-list-item-title">
                      <div class="text-body-1 text-high-emphasis py-1 text-truncate w-100 cursor-pointer">
                        {{ preset.label }}
                      </div>
                    </div>
                  </div>

                  <!-- Кнопка редактирования пресета -->
                  <div class="v-list-item-action v-list-item-action--end ms-2 d-flex align-center flex-shrink-0">
                    <IconBtn
                      size="x-small"
                      color="secondary"
                      variant="text"
                      @click.stop="openEditPresetDialog(preset)"
                    >
                      <VIcon icon="ri-pencil-line" size="16" />
                      <VTooltip activator="parent" location="bottom">
                        Редактировать фильтр
                      </VTooltip>
                    </IconBtn>
                  </div>
                </div>

                <div
                  v-if="filteredPresets.length === 0"
                  class="py-3 text-center text-caption text-disabled"
                >
                  Ничего не найдено
                </div>
              </div>
            </VExpansionPanelText>
          </VExpansionPanel>

          <VExpansionPanel
            v-for="filter in filters"
            :key="filter.key"
            :value="filter.key"
            class="filter-panel"
          >
            <!-- Заголовок секции фильтра -->
            <VExpansionPanelTitle class="py-2 px-4">
              <div class="d-flex align-center justify-space-between w-100 me-2 min-w-0">
                <div class="d-flex align-center min-w-0 flex-grow-1 me-1">
                  <VIcon
                    v-if="filter.icon"
                    :icon="filter.icon"
                    size="18"
                    class="me-2 text-medium-emphasis flex-shrink-0"
                  />
                  <span class="filter-panel-title">{{ filter.label }}</span>
                </div>
                <div class="d-flex align-center gap-1 flex-shrink-0">
                  <VChip
                    v-if="getSelectedCount(filter.key) > 0"
                    size="x-small"
                    color="primary"
                    variant="tonal"
                    class="font-weight-medium"
                  >
                    {{ getSelectedCount(filter.key) }}
                  </VChip>
                  <IconBtn
                    v-if="getSelectedCount(filter.key) > 0"
                    size="x-small"
                    color="secondary"
                    variant="text"
                    @click.stop="onResetFilter(filter.key)"
                  >
                    <VIcon icon="ri-delete-bin-line" size="16" />
                    <VTooltip activator="parent" location="bottom">
                      Сбросить фильтр
                    </VTooltip>
                  </IconBtn>
                </div>
              </div>
            </VExpansionPanelTitle>

            <!-- Содержимое секции: DashboardFilterTree -->
            <VExpansionPanelText class="pa-0">
              <DashboardFilterTree
                :field-key="filter.key"
                :store="graphStore"
                :model-value="selectedFilterIds[filter.key] || []"
                :placeholder="`Поиск (${String(filter.label || '').toLowerCase()})...`"
                @change="onFilterChange(filter.key, $event)"
              />
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </div>
    </VCard>

    <!-- Ресайзер справа при расположении сайдбара слева -->
    <div
      v-if="!isCollapsed && !display.smAndDown.value && sidebarPosition === 'left'"
      class="filter-sidebar-resizer resizer-right"
      :class="{ 'is-resizing': isResizing }"
      @mousedown="startResize"
    />
  </div>

  <!-- МОБИЛЬНАЯ ВЕРСИЯ: Оверлей-сайдбар на всю высоту экрана (100vh) через Teleport -->
  <Teleport v-else to="body">
    <div v-if="localIsDrawerOpen" class="dashboard-mobile-drawer-container">
      <!-- Затемнение фона (scrim) -->
      <Transition name="drawer-scrim">
        <div
          v-if="localIsDrawerOpen"
          class="dashboard-mobile-drawer-scrim"
          @click="localIsDrawerOpen = false"
        />
      </Transition>

      <!-- Панель сайдбара во всю высоту (100vh / 100dvh) -->
      <Transition name="drawer-slide">
        <div
          v-if="localIsDrawerOpen"
          class="dashboard-mobile-drawer-panel d-flex flex-column"
        >
          <!-- Шапка мобильного сайдбара с кнопками управления -->
          <div class="sidebar-header d-flex align-center justify-space-between px-4 py-3">
            <div class="d-flex align-center gap-2 text-truncate">
              <VIcon icon="ri-filter-3-line" size="20" color="primary" />
              <span class="text-subtitle-1 font-weight-bold text-truncate">Фильтры</span>
              <VChip
                v-if="activeFiltersCount > 0"
                size="x-small"
                color="primary"
                variant="flat"
              >
                {{ activeFiltersCount }}
              </VChip>
            </div>

            <div class="d-flex align-center gap-1">
              <!-- Кнопка развернуть / свернуть все панели -->
              <IconBtn
                size="small"
                @click.stop="toggleExpandAllPanels"
              >
                <VIcon :icon="expandAllPanelsIcon" size="20" />
                <VTooltip activator="parent" location="bottom">
                  {{ expandAllPanelsTooltip }}
                </VTooltip>
              </IconBtn>

              <!-- Кнопка закрытия сайдбара -->
              <IconBtn
                size="small"
                @click="localIsDrawerOpen = false"
              >
                <VIcon icon="ri-close-line" size="22" />
                <VTooltip activator="parent" location="bottom">
                  Закрыть
                </VTooltip>
              </IconBtn>
            </div>
          </div>

          <VDivider />

          <div class="sidebar-content flex-grow-1 overflow-y-auto">
            <!-- Скелетоны при начальной загрузке -->
            <div v-if="loading && (!filters || !filters.length)" class="pa-4 d-flex flex-column gap-3">
              <VSkeletonLoader v-for="i in 3" :key="i" type="heading" class="rounded" />
            </div>

            <!-- Сообщение об отсутствии фильтров -->
            <div
              v-else-if="!filters || !filters.length"
              class="pa-4 text-caption text-disabled text-center"
            >
              Нет доступных фильтров
            </div>

            <!-- Аккордеон фильтров -->
            <VExpansionPanels
              v-else
              v-model="openedPanels"
              multiple
              variant="accordion"
              class="filter-expansion-panels"
              @update:model-value="onPanelChange"
            >
              <!-- Аккордеон: Сохраненные фильтры для мобильной шторки -->
              <VExpansionPanel
                v-if="targetPresets.length > 0"
                value="__saved_presets__"
                class="filter-panel"
              >
                <VExpansionPanelTitle class="py-2 px-4">
                  <div class="d-flex align-center justify-space-between w-100 me-2 min-w-0">
                    <div class="d-flex align-center min-w-0 flex-grow-1 me-1">
                      <VIcon
                        icon="ri-save-line"
                        size="18"
                        class="me-2 text-medium-emphasis flex-shrink-0"
                      />
                      <span class="filter-panel-title">Сохраненные фильтры</span>
                    </div>
                    <div class="d-flex align-center gap-1 flex-shrink-0">
                      <VChip
                        size="x-small"
                        color="primary"
                        variant="tonal"
                        class="font-weight-medium"
                      >
                        {{ targetPresets.length }}
                      </VChip>
                      <IconBtn
                        v-if="hasActivePreset || activeFiltersCount > 0"
                        size="x-small"
                        color="secondary"
                        variant="text"
                        @click.stop="onResetAll"
                      >
                        <VIcon icon="ri-delete-bin-line" size="16" />
                        <VTooltip activator="parent" location="bottom">
                          Сбросить фильтр
                        </VTooltip>
                      </IconBtn>
                    </div>
                  </div>
                </VExpansionPanelTitle>

                <VExpansionPanelText class="pa-0">
                  <!-- Поле поиска для сохраненных фильтров -->
                  <div class="filter-search-wrapper px-3 pt-2 pb-1">
                    <VTextField
                      v-model="presetSearch"
                      placeholder="Поиск (сохраненные фильтры)..."
                      variant="outlined"
                      density="compact"
                      :append-inner-icon="presetSearch ? undefined : 'ri-search-line'"
                      hide-details
                      clearable
                      class="search-field mb-2"
                      @click:clear="presetSearch = ''"
                    />
                  </div>

                  <div class="v-list custom-treeview px-2 pb-1">
                    <div
                      v-for="preset in filteredPresets"
                      :key="preset.id"
                      class="v-list-item filter-tree-node d-flex align-center py-1 px-1 rounded cursor-pointer"
                      :class="{
                        'v-list-item--active': isPresetActive(preset, graphStore.activeFilters, graphStore.groupBy, graphStore.dateTrunc),
                        'tree-item--selected': isPresetActive(preset, graphStore.activeFilters, graphStore.groupBy, graphStore.dateTrunc),
                        'tree-item--leaf': true,
                      }"
                      style="padding-inline-start: 6px !important;"
                      @click.stop="togglePreset(preset)"
                    >
                      <!-- Фиксированный спейсер листа для выравнивания с чекбоксами дерева -->
                      <div class="v-list-item-action v-list-item-action--start me-1 d-flex align-center justify-center flex-shrink-0">
                        <div class="node-leaf-spacer" />
                      </div>

                      <!-- Чекбокс выбора -->
                      <div class="node-checkbox-wrapper me-2" @click.stop="togglePreset(preset)">
                        <VCheckboxBtn
                          :model-value="isPresetActive(preset, graphStore.activeFilters, graphStore.groupBy, graphStore.dateTrunc)"
                          color="primary"
                          density="compact"
                          class="node-checkbox"
                        />
                      </div>

                      <!-- Название пресета -->
                      <div class="v-list-item__content flex-grow-1 min-width-0">
                        <div class="v-list-item-title">
                          <div class="text-body-1 text-high-emphasis py-1 text-truncate w-100 cursor-pointer">
                            {{ preset.label }}
                          </div>
                        </div>
                      </div>

                      <!-- Кнопка редактирования пресета -->
                      <div class="v-list-item-action v-list-item-action--end ms-2 d-flex align-center flex-shrink-0">
                        <IconBtn
                          size="x-small"
                          color="secondary"
                          variant="text"
                          @click.stop="openEditPresetDialog(preset)"
                        >
                          <VIcon icon="ri-pencil-line" size="16" />
                          <VTooltip activator="parent" location="bottom">
                            Редактировать фильтр
                          </VTooltip>
                        </IconBtn>
                      </div>
                    </div>

                    <div
                      v-if="filteredPresets.length === 0"
                      class="py-3 text-center text-caption text-disabled"
                    >
                      Ничего не найдено
                    </div>
                  </div>
                </VExpansionPanelText>
              </VExpansionPanel>

              <VExpansionPanel
                v-for="filter in filters"
                :key="filter.key"
                :value="filter.key"
                class="filter-panel"
              >
                <!-- Заголовок секции фильтра -->
                <VExpansionPanelTitle class="py-2 px-4">
                  <div class="d-flex align-center justify-space-between w-100 me-2 min-w-0">
                    <div class="d-flex align-center min-w-0 flex-grow-1 me-1">
                      <VIcon
                        v-if="filter.icon"
                        :icon="filter.icon"
                        size="18"
                        class="me-2 text-medium-emphasis flex-shrink-0"
                      />
                      <span class="filter-panel-title">{{ filter.label }}</span>
                    </div>
                    <div class="d-flex align-center gap-1 flex-shrink-0">
                      <VChip
                        v-if="getSelectedCount(filter.key) > 0"
                        size="x-small"
                        color="primary"
                        variant="tonal"
                        class="font-weight-medium"
                      >
                        {{ getSelectedCount(filter.key) }}
                      </VChip>
                      <IconBtn
                        v-if="getSelectedCount(filter.key) > 0"
                        size="x-small"
                        color="secondary"
                        variant="text"
                        @click.stop="onResetFilter(filter.key)"
                      >
                        <VIcon icon="ri-delete-bin-line" size="16" />
                        <VTooltip activator="parent" location="bottom">
                          Сбросить фильтр
                        </VTooltip>
                      </IconBtn>
                    </div>
                  </div>
                </VExpansionPanelTitle>

                <!-- Содержимое секции: DashboardFilterTree -->
                <VExpansionPanelText class="pa-0">
                  <DashboardFilterTree
                    :field-key="filter.key"
                    :store="graphStore"
                    :model-value="selectedFilterIds[filter.key] || []"
                    :placeholder="`Поиск (${String(filter.label || '').toLowerCase()})...`"
                    @change="onFilterChange(filter.key, $event)"
                  />
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>

  <!-- Диалог редактирования / удаления сохраненного фильтра -->
  <VDialog
    v-model="isEditPresetDialogOpen"
    max-width="500"
  >
    <VCard class="pa-6">
      <div class="d-flex justify-space-between align-center mb-6">
        <span class="text-h4 font-weight-medium">Редактировать фильтр</span>
        <CustomCloseButton :close-action="() => isEditPresetDialogOpen = false" />
      </div>

      <VTextField
        v-model="editingPresetLabel"
        label="Название фильтра"
        placeholder="Например, Все НС за 25-26"
        variant="outlined"
        persistent-placeholder
        autofocus
        clearable
        :error-messages="editPresetError"
        @keyup.enter="handleUpdatePreset"
        @input="editPresetError = ''"
      />

      <div class="d-flex justify-end gap-3 mt-6">
        <VBtn
          color="error"
          variant="outlined"
          prepend-icon="ri-delete-bin-line"
          @click="handleDeletePreset"
        >
          Удалить
        </VBtn>

        <VBtn
          color="primary"
          variant="flat"
          :disabled="!editingPresetLabel.trim()"
          @click="handleUpdatePreset"
        >
          Сохранить
        </VBtn>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.dashboard-mobile-drawer-container {
  position: fixed;
  inset: 0;
  z-index: 1050;
  pointer-events: none;
}

.dashboard-mobile-drawer-scrim {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 20, 34, 0.48);
  backdrop-filter: blur(2px);
  z-index: 1051;
  pointer-events: auto;
}

.dashboard-mobile-drawer-panel {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  width: 320px;
  max-width: 85vw;
  height: 100vh;
  height: 100dvh;
  background-color: rgb(var(--v-theme-surface));
  z-index: 1052;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.25);
  pointer-events: auto;
  overflow: hidden;
}

.drawer-scrim-enter-active,
.drawer-scrim-leave-active {
  transition: opacity 0.25s ease;
}

.drawer-scrim-enter-from,
.drawer-scrim-leave-to {
  opacity: 0;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.28s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(-100%);
}

.dashboard-filter-sidebar-wrapper {
  position: relative;
  align-self: stretch;
  display: flex;
  flex-shrink: 0;
  flex-grow: 0;
  overflow: hidden;
  min-height: 520px;
  transition: width 0.28s cubic-bezier(0.25, 1, 0.5, 1),
              flex-basis 0.28s cubic-bezier(0.25, 1, 0.5, 1);
  z-index: 5;

  @media (min-width: 961px) {
    min-height: calc(100vh - 160px);
  }

  &.is-resizing {
    transition: none !important;
  }
}

.sidebar-card {
  height: 100%;
  flex: 1 1 0;
  min-width: 0;
  width: auto;
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-on-surface), var(--v-custom-opacity, 0.08));
  background-color: rgb(var(--v-theme-surface));
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.collapsed-rail {
  width: 100%;
  background-color: rgb(var(--v-theme-surface));
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--v-theme-on-surface), 0.15);
    border-radius: 2px;
  }
}

.rail-filter-item {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  transition: background-color 0.15s ease, transform 0.1s ease;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.08);
  }

  &:active {
    transform: scale(0.95);
  }

  &.has-active {
    background-color: rgba(var(--v-theme-primary), 0.08);

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.16);
    }
  }

  &.rail-reset-item {
    &:hover {
      background-color: rgba(var(--v-theme-error), 0.12);
    }
  }
}

.rail-inactive-icon {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity, 0.6));
  transition: color 0.15s ease;

  .rail-filter-item:hover & {
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity, 0.87));
  }
}

.sidebar-header {
  flex-shrink: 0;
  min-height: 48px;
  height: 48px;
  padding: 0 16px;
  overflow: hidden;
  white-space: nowrap;
  box-sizing: border-box;
}

.sidebar-nav-toggle {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  transition: opacity 0.15s ease, background-color 0.15s ease;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.06);
  }
}

.sidebar-pos-toggle {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  transition: color 0.15s ease, transform 0.15s ease;

  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}

.reset-btn {
  font-size: 11px;
}

.sidebar-content {
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  flex: 1 1 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--v-theme-on-surface), 0.15);
    border-radius: 4px;
  }
}

.filter-expansion-panels {
  :deep(.v-expansion-panel) {
    background-color: transparent;

    &::before {
      opacity: 0.06;
    }
  }

  :deep(.v-expansion-panel-title) {
    min-height: 44px;
    height: auto;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 16px;
    padding-right: 16px;
  }

  :deep(.v-expansion-panel-title__icon) {
    flex-shrink: 0;
    margin-inline-start: 8px;
  }

  :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 !important;
  }
}

.filter-panel-title {
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  letter-spacing: 0.15px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
}

.search-field {
  :deep(.v-field) {
    border-radius: 8px;
  }
}

.custom-treeview {
  background: transparent !important;

  .v-list-item {
    min-height: 36px;
    padding-top: 2px;
    padding-bottom: 2px;
    padding-right: 8px;
    border-radius: 6px;
    margin-bottom: 2px;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.12s ease;

    &:hover {
      background-color: rgba(var(--v-theme-on-surface), 0.04);
    }

    &.v-list-item--active,
    &.tree-item--selected {
      background-color: rgba(var(--v-theme-primary), 0.08);

      :deep(.v-list-item-title),
      :deep(.v-list-item-title div) {
        color: rgb(var(--v-theme-primary)) !important;
        font-weight: 500 !important;
      }
    }
  }

  :deep(.v-list-item-title),
  :deep(.v-list-item-title div) {
    font-size: 14px !important;
    font-weight: 400 !important;
    line-height: 20px !important;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  }

  .node-checkbox-wrapper {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .node-checkbox {
    :deep(.v-selection-control) {
      min-height: auto;
    }
  }
}

/*
 * Разделитель между сайдбаром и контентом
 */
.filter-sidebar-resizer {
  flex-shrink: 0;
  width: 6px;
  cursor: col-resize;
  background-color: rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 3px;
  transition: background-color 0.15s ease;

  &.resizer-right {
    margin-left: 4px;
    margin-right: 0;
  }

  &.resizer-left {
    margin-right: 4px;
    margin-left: 0;
  }

  &:hover,
  &.is-resizing {
    background-color: rgb(var(--v-theme-primary));
  }
}
</style>


